import {
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { Avatar, Box, Card, CardContent, CardHeader, Chip, Divider, Grid, Typography } from '@mui/material';
import React from 'react';
import { DayOfWeek, Teacher } from '../../types';

interface TeacherCardProps {
  teacher: Teacher;
  showAvailability?: boolean;
  showPreferences?: boolean;
}

const dayLabels: Record<DayOfWeek, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
};

export const TeacherCard: React.FC<TeacherCardProps> = ({
  teacher,
  showAvailability = true,
  showPreferences = false,
}) => {
  const getInitials = () => {
    return `${teacher.firstName[0]}${teacher.lastName[0]}`.toUpperCase();
  };

  const getTotalAvailablePeriods = () => {
    return Object.values(teacher.availability).reduce(
      (total, day) => total + (day.available ? day.periods.length : 0),
      0,
    );
  };

  return (
    <Card>
      <CardHeader
        avatar={
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 56,
              height: 56,
              fontSize: '1.5rem',
            }}
          >
            {getInitials()}
          </Avatar>
        }
        title={
          <Typography variant="h6">
            {teacher.firstName} {teacher.lastName}
          </Typography>
        }
        subheader={
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
            {teacher.roles.map((role) => (
              <Chip
                key={role}
                label={role}
                size="small"
                color={
                  role === 'SLT' || role === 'HeadTeacher'
                    ? 'primary'
                    : role === 'HoD'
                      ? 'secondary'
                      : role === 'HoY'
                        ? 'info'
                        : 'default'
                }
              />
            ))}
          </Box>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          {/* Contact Information */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2">{teacher.email}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">{teacher.phone}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Subjects */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SchoolIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">Subjects</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {teacher.subjects.map((subject) => (
                <Chip key={subject} label={subject} size="small" variant="outlined" />
              ))}
            </Box>
          </Grid>

          {/* Qualifications */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AssignmentIcon fontSize="small" color="action" />
              <Typography variant="subtitle2">Qualifications</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {teacher.qualifications.map((qual, index) => (
                <Chip key={index} label={qual} size="small" variant="outlined" />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Contract Details */}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Contract Type
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={teacher.contractType}
                size="small"
                color={
                  teacher.contractType === 'full-time'
                    ? 'success'
                    : teacher.contractType === 'part-time'
                      ? 'warning'
                      : 'default'
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Max Periods per Week
            </Typography>
            <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
              {teacher.maxPeriodsPerWeek} periods
            </Typography>
          </Grid>

          {teacher.contractedDays && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Contracted Days
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                {teacher.contractedDays.map((day) => (
                  <Chip key={day} label={dayLabels[day]} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}

          {/* Availability Summary */}
          {showAvailability && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2">Availability</Typography>
                </Box>

                <Grid container spacing={1}>
                  {(Object.keys(teacher.availability) as DayOfWeek[]).map((day) => {
                    const dayAvail = teacher.availability[day];
                    return (
                      <Grid item xs={12} key={day}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                            bgcolor: dayAvail.available ? 'success.50' : 'grey.100',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={500}>
                            {dayLabels[day]}
                          </Typography>
                          {dayAvail.available ? (
                            <Typography variant="caption">Periods: {dayAvail.periods.join(', ')}</Typography>
                          ) : (
                            <Chip label="Unavailable" size="small" />
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Available Periods: {getTotalAvailablePeriods()}
                  </Typography>
                </Box>
              </Grid>
            </>
          )}

          {/* Preferences */}
          {showPreferences && teacher.preferences && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Scheduling Preferences
                </Typography>

                {teacher.preferences.preferredDays.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Preferred Days
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {teacher.preferences.preferredDays.map((day) => (
                        <Chip key={day} label={dayLabels[day]} size="small" color="success" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {teacher.preferences.avoidedDays.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Avoided Days
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {teacher.preferences.avoidedDays.map((day) => (
                        <Chip key={day} label={dayLabels[day]} size="small" color="error" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {teacher.preferences.maxConsecutiveLessons && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Max Consecutive Lessons: {teacher.preferences.maxConsecutiveLessons}
                  </Typography>
                )}

                {teacher.preferences.minBreakBetweenLessons && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Min Break Between Lessons: {teacher.preferences.minBreakBetweenLessons}
                  </Typography>
                )}

                {teacher.preferences.notes && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">{teacher.preferences.notes}</Typography>
                  </Box>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
